import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { goalSettings, stepConfigItem } from '../types';
import { dietSettingsIsLastStep } from '../utils/DietSettingsIsLastStep';
import { dietSettingsGetNextStep } from '../utils/DietSettingsNextStepAction';
import { dietSettingsGetPreviousStep } from '../utils/DietSettingsPreviousStepAction';
import { dietSettingsGetSchemaForParameters } from '../utils/DietSettingsSchemaForParametersAction';
import StepContext from './StepContext';

interface StepProviderProps {
  currentMainStep: string;
  schema: stepConfigItem[];
  children: React.ReactNode;
  saveGoalSettings: (values: goalSettings) => void;
  validateGoalSettings: (values: goalSettings) => void;
}

const StepProvider: React.FC<StepProviderProps> = ({
  currentMainStep,
  schema,
  children,
  saveGoalSettings,
  validateGoalSettings,
}) => {
  const params = useParams();
  const stepFromUrl = params['*'];
  const { values: goalSettings } = useFormikContext<goalSettings>();
  const schemaForParameters = dietSettingsGetSchemaForParameters(schema, goalSettings);

  const [currentStep, setCurrentStep] = useState(
    schemaForParameters.find((step) => step.slug === stepFromUrl)?.id || ''
  );

  useEffect(() => {
    setCurrentStep(schemaForParameters.find((step) => step.slug === stepFromUrl)?.id || '');
  }, [params, stepFromUrl, schema, schemaForParameters]);

  const prevStep = dietSettingsGetPreviousStep(schemaForParameters, goalSettings, currentStep);
  const nextStep = dietSettingsGetNextStep(schemaForParameters, goalSettings, currentStep);
  const isLastStep = dietSettingsIsLastStep(schema, goalSettings, currentStep);
  const shouldBackendValidate = schemaForParameters.find(
    (step) => step.slug === stepFromUrl
  )?.shouldBackendValidate;

  return (
    <StepContext.Provider
      value={{
        currentStep: currentStep,
        currentMainStep: currentMainStep,
        schema: schema,
        prevStep: prevStep,
        nextStep: nextStep,
        isLastStep: isLastStep,
        shouldBackendValidate: shouldBackendValidate,
        saveGoalSettings: saveGoalSettings,
        validateGoalSettings: validateGoalSettings,
      }}
    >
      {children}
    </StepContext.Provider>
  );
};

export default StepProvider;
